import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Alert,
    Box,
    Button,
    Card,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';

import Loading from '../../../../components/loading';
import { RootState } from '../../../../store';
import { GT_COLORS } from '../../../../constants/designTokens.constants';
import { downloadCsv } from '../../../../hooks/useCsvDownload';
import { permissionCheck } from '../../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../../constants/systemPermissions.constants';
import { useTestModeReport } from '../testMode.api';
import { ReportParams, WhoTestedRow } from '../testMode.types';
import { TEST_MODE_COPY } from '../testMode.copy';

const COPY = TEST_MODE_COPY.report;

/**
 * Onboarding checklist steps shown in the report. "Attended training" from
 * the design is intentionally absent for MVP — no data source exists yet
 * (open question with product); it slots in here when one does.
 */
const STEPS: Array<{ key: keyof Pick<WhoTestedRow, 'registered' | 'paired' | 'testedApp' | 'testedDevice'>; header: string }> = [
    { key: 'registered', header: COPY.headers.registered },
    { key: 'paired', header: COPY.headers.paired },
    { key: 'testedApp', header: COPY.headers.testedApp },
    { key: 'testedDevice', header: COPY.headers.testedDevice },
];

const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    return `${parts[0]?.charAt(0) ?? ''}${parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''}`.toUpperCase() || '?';
};

const selectSx = {
    height: 36,
    fontSize: 13,
    borderRadius: '9px',
    backgroundColor: 'background.paper',
} as const;

/**
 * Zone 3: the onboarding-progress report. Broader than the live roster (the
 * "how did last week's onboardings go?" view). Filters apply immediately —
 * no staged Apply down here, matching the prototype.
 */
export const ProgressReport: React.FC = () => {
    const theme = useTheme();
    const border = `1px solid ${alpha(theme.palette.grey[500], 0.16)}`;

    const [period, setPeriod] = useState<ReportParams['period']>('30d');
    const [groupId, setGroupId] = useState('');
    const [teamId, setTeamId] = useState('');

    const { items: allTeams } = useSelector((state: RootState) => state.teams);
    const { items: allGroups } = useSelector((state: RootState) => state.groups);
    const canFilterByGroup = permissionCheck(customerPermissionFlags.GROUP_LEADER);

    const teams = useMemo(() => {
        let list = allTeams ?? [];
        if (groupId) list = list.filter((t: any) => t.groupId === groupId);
        return list;
    }, [allTeams, groupId]);

    const params = useMemo<ReportParams>(
        () => ({ period, ...(groupId ? { groupId } : {}), ...(teamId ? { teamId } : {}) }),
        [period, groupId, teamId],
    );
    const report = useTestModeReport(params);
    const rows = report.data ?? [];

    const exportReport = () => {
        try {
            downloadCsv(
                rows.map((r) => ({
                    name: r.name,
                    group: r.group ?? '',
                    team: r.team ?? '',
                    registered: r.registered ? 'yes' : 'no',
                    pairedDevice: r.paired ? 'yes' : 'no',
                    testedApp: r.testedApp ? 'yes' : 'no',
                    testedDevice: r.testedDevice ? 'yes' : 'no',
                    sessions: r.sessionCount,
                    lastEnteredAt: r.lastEnteredAt ?? '',
                })),
                `${COPY.csvFileName}-${dayjs().format('YYYY-MM-DD')}`,
            );
        } catch (err) {
            console.error('[TestMode] report CSV export failed', err);
            toast.error(TEST_MODE_COPY.errors.exportCsv);
        }
    };

    const headerSx = { fontSize: 12.5, fontWeight: 600, color: 'grey.700', whiteSpace: 'nowrap' } as const;
    const checkOrDash = (done: boolean) =>
        done ? (
            <CheckIcon sx={{ fontSize: 17, color: 'success.dark' }} />
        ) : (
            <Typography component="span" sx={{ color: 'grey.400', fontSize: 15 }}>–</Typography>
        );

    return (
        <Card sx={{ mt: '28px', borderRadius: '16px', border, overflow: 'hidden' }}>
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                flexWrap="wrap"
                gap="14px"
                sx={{ p: '20px 22px', borderBottom: border }}
            >
                <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: (theme.palette.primary as any).darker ?? theme.palette.primary.dark }}>
                        {COPY.title}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: '6px', maxWidth: 520, lineHeight: 1.5 }}>
                        {COPY.description}
                    </Typography>
                </Box>
                <Stack direction="row" alignItems="center" gap="10px" flexWrap="wrap">
                    {canFilterByGroup && (
                        <Select
                            size="small"
                            value={groupId}
                            onChange={(e) => { setGroupId(e.target.value); setTeamId(''); }}
                            displayEmpty
                            sx={selectSx}
                        >
                            <MenuItem value="">{COPY.allGroups}</MenuItem>
                            {(allGroups ?? []).map((g: any) => (
                                <MenuItem key={g.id || g._id} value={g.id || g._id}>{g.name}</MenuItem>
                            ))}
                        </Select>
                    )}
                    <Select
                        size="small"
                        value={teamId}
                        onChange={(e) => setTeamId(e.target.value)}
                        displayEmpty
                        sx={selectSx}
                    >
                        <MenuItem value="">{COPY.allTeams}</MenuItem>
                        {teams.map((t: any) => (
                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))}
                    </Select>
                    <Select
                        size="small"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value as ReportParams['period'])}
                        sx={selectSx}
                    >
                        {COPY.rangeOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </Select>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={exportReport}
                        disabled={rows.length === 0}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
                        sx={{ height: 36, borderRadius: '9px', fontWeight: 600, fontSize: 13, boxShadow: 'none' }}
                    >
                        {COPY.exportCsv}
                    </Button>
                </Stack>
            </Stack>

            {report.isLoading ? (
                <Loading />
            ) : report.isError ? (
                <Alert severity="error" sx={{ m: '16px 22px' }}>
                    {TEST_MODE_COPY.errors.report}
                </Alert>
            ) : rows.length === 0 ? (
                <Typography sx={{ p: '40px 22px', textAlign: 'center', fontSize: 13.5, color: 'text.disabled' }}>
                    {COPY.empty}
                </Typography>
            ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 860 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                <TableCell sx={headerSx}>{COPY.headers.user}</TableCell>
                                <TableCell sx={headerSx}>{COPY.headers.groupTeam}</TableCell>
                                {STEPS.map((s) => (
                                    <TableCell key={s.key} sx={{ ...headerSx, textAlign: 'center' }}>{s.header}</TableCell>
                                ))}
                                <TableCell sx={headerSx}>{COPY.headers.progress}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const done = STEPS.filter((s) => Boolean(row[s.key])).length;
                                return (
                                    <TableRow key={row.userId} hover sx={{ '& td': { borderTop: border, borderBottom: 'none' } }}>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" gap="10px">
                                                <Box
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: '50%',
                                                        backgroundColor: GT_COLORS.primary,
                                                        color: 'common.white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {getInitials(row.name)}
                                                </Box>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: (theme.palette.primary as any).darker ?? theme.palette.primary.dark }}>
                                                    {row.name}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: 13 }}>{row.group ?? '—'}</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: '2px' }}>{row.team ?? '—'}</Typography>
                                        </TableCell>
                                        {STEPS.map((s) => (
                                            <TableCell key={s.key} align="center">{checkOrDash(Boolean(row[s.key]))}</TableCell>
                                        ))}
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" gap="8px">
                                                <Stack direction="row" gap="3px">
                                                    {STEPS.map((s, i) => (
                                                        <Box
                                                            key={s.key}
                                                            sx={{
                                                                width: 14,
                                                                height: 6,
                                                                borderRadius: '3px',
                                                                backgroundColor: i < done ? 'success.main' : 'grey.300',
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                                                    {COPY.progressLabel(done, STEPS.length)}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Card>
    );
};
